import React, {createContext, ReactNode, useContext, useState} from 'react';
import remoteConfig from '@react-native-firebase/remote-config';
import {api} from '../services/api';
import { set } from 'date-fns';

interface GlobalContextData {
  configIndiqueGanhe: ConfigIndiqueGanheType[];
  setConfigIndiqueGanhe: React.Dispatch<React.SetStateAction<ConfigIndiqueGanheType[]>>;
  fetchConfigIndiqueGanhe: () => Promise<void>;
  setProdStripe: React.Dispatch<React.SetStateAction<boolean>>;
  loadDisabledHighlights: () => Promise<void>;
  fetchRemoteConfigCupom: () => Promise<void>;
  fetchRemoteConfigTaxaConv: () => Promise<void>;
  fetchTextoIndParcDebCombustivel: () => Promise<void>;
  fetchPixDebitoVeicular: () => Promise<void>;
  fetchBannerFlags: () => Promise<void>;
  fetchPrazoDebitosPayment: () => Promise<void>;
  fetchValorMaximoEmprestimo: () => Promise<void>;
  prodStripe: boolean;
  disabledFeatures: string[];
  telaNovaCupom: boolean;
  ativaTaxaConv: boolean;
  textoIndParcDebCombustivel: string;
  pixDebitoVeicular: boolean;
  exibeBannerPosteEGanhe: boolean;
  prazoDebitosPayment: string;
  valorEmprestimo: number;
}

interface GlobalProviderProps {
  children: ReactNode;
}

type ConfigIndiqueGanheType = {
  limite_indicados: number;
  titulo: string;
  descricao: string;
};

const GlobalContext = createContext<GlobalContextData>({} as GlobalContextData);

function GlobalProvider({children}: GlobalProviderProps) {
  const [configIndiqueGanhe, setConfigIndiqueGanhe] = React.useState([] as ConfigIndiqueGanheType[]);
  const [prodStripe, setProdStripe] = React.useState(false);
  const [disabledFeatures, setDisabledFeatures] = React.useState<string[]>([]);
  const [telaNovaCupom, setTelaNovaCupom] = React.useState(false);
  const [ativaTaxaConv, setAtivaTaxaConv] = React.useState(false);
  const [textoIndParcDebCombustivel, setTextoIndParcDebCombustivel] = useState('');
  const [pixDebitoVeicular, setPixDebitoVeicular] = useState(false);
  const [exibeBannerPosteEGanhe, setExibeBannerPosteEGanhe] = useState(true);
  const [prazoDebitosPayment, setPrazoDebitosPayment] = useState('1 a 2 dias');
  const [valorEmprestimo, setValorEmprestimo] = useState(0);

  async function fetchRemoteConfigCupom() {
    try {
      await remoteConfig().setDefaults({tela_nova_cupom_2: false});
      await remoteConfig().setConfigSettings({minimumFetchIntervalMillis: 0});
      await remoteConfig().fetchAndActivate();
      const value = remoteConfig().getValue('tela_nova_cupom_2').asBoolean();
      setTelaNovaCupom(value);
      console.log('[RemoteConfig] tela_nova_cupom:', value);
    } catch (error) {
      console.warn('[RemoteConfig] Erro ao buscar tela_nova_cupom:', error);
    }
  }

  async function fetchRemoteConfigTaxaConv() {
    try {
      await remoteConfig().setDefaults({exibe_taxa_conv: false});
      await remoteConfig().setConfigSettings({minimumFetchIntervalMillis: 0});
      await remoteConfig().fetchAndActivate();
      const value = remoteConfig().getValue('exibe_taxa_conv').asBoolean();
      setAtivaTaxaConv(value);
      console.log('[RemoteConfig] exibe_taxa_conv:', value);
    } catch (error) {
      console.warn('[RemoteConfig] Erro ao buscar exibe_taxa_conv:', error);
    }
  }

  async function fetchPixDebitoVeicular() {
    try {
      await remoteConfig().setDefaults({pix_debito_veicular: false});
      await remoteConfig().setConfigSettings({minimumFetchIntervalMillis: 0});
      await remoteConfig().fetchAndActivate();
      const value = remoteConfig().getValue('pix_debito_veicular').asBoolean();
      setPixDebitoVeicular(value);
      console.log('[RemoteConfig] pix_debito_veicular:', value);
    } catch (error) {
      console.warn('[RemoteConfig] Erro ao buscar pix_debito_veicular:', error);
    }
  }

  async function fetchPrazoDebitosPayment() {
    try {
      await remoteConfig().setDefaults({prazo_debitos_payment: ''});
      await remoteConfig().setConfigSettings({minimumFetchIntervalMillis: 0});
      await remoteConfig().fetchAndActivate();
      const raw = remoteConfig().getValue('prazo_debitos_payment').asString();
      const parsed: {type: string; value: string}[] = JSON.parse(raw);
      const text = parsed.filter(item => item.type === 'text').map(item => item.value).join('');
      if (text) {setPrazoDebitosPayment(text);}
      console.log('[RemoteConfig] prazo_debitos_payment:', text);
    } catch (error) {
      console.warn('[RemoteConfig] Erro ao buscar prazo_debitos_payment:', error);
    }
  }

  async function fetchTextoIndParcDebCombustivel() {
    try {
      await remoteConfig().setDefaults({texto_ind_parc_deb_combustivel: ''});
      await remoteConfig().setConfigSettings({minimumFetchIntervalMillis: 0});
      await remoteConfig().fetchAndActivate();
      const value = remoteConfig().getValue('texto_ind_parc_deb_combustivel').asString();
      setTextoIndParcDebCombustivel(value);
      console.log('[RemoteConfig] texto_ind_parc_deb_combustivel:', value);
    } catch (error) {
      console.warn('[RemoteConfig] Erro ao buscar texto_ind_parc_deb_combustivel:', error);
    }
  }

  async function fetchBannerFlags() {
    try {
      await remoteConfig().setDefaults({
        exibe_banner_poste_e_ganhe: true,
      });
      await remoteConfig().setConfigSettings({minimumFetchIntervalMillis: 0});
      await remoteConfig().fetchAndActivate();
      
      const posteEGanhe = remoteConfig().getValue('exibe_banner_poste_e_ganhe').asBoolean();
      setExibeBannerPosteEGanhe(posteEGanhe);
      console.log('[RemoteConfig] exibe_banner_poste_e_ganhe:', posteEGanhe);
    } catch (error) {
      console.warn('[RemoteConfig] Erro ao buscar banner flags:', error);
    }
  }

  async function fetchValorMaximoEmprestimo() {
    try {
      await remoteConfig().setDefaults({valor_maximo_emprestimo: 0});
      await remoteConfig().setConfigSettings({minimumFetchIntervalMillis: 0});
      await remoteConfig().fetchAndActivate();
      const value = remoteConfig().getValue('valor_maximo_emprestimo').asNumber();
      setValorEmprestimo(value);
      console.log('[RemoteConfig] valor_maximo_emprestimo:', value);
    } catch (error) {
      console.warn('[RemoteConfig] Erro ao buscar valor_maximo_emprestimo:', error);
    }
  }

  async function fetchConfigIndiqueGanhe() {
    try {
      const response = await api.get('/regraLimiteIndicacoes');
      setConfigIndiqueGanhe(response.data);
    } catch (error) {
      console.error('Error fetching configIndiqueGanhe:', error);
    }
  }

  const loadDisabledHighlights = async () => {
    try {
      const response = await api.get('/icones');
      setDisabledFeatures(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar highlights desabilitados:', error);
    }
  };

  return (
    <GlobalContext.Provider
      value={{
        configIndiqueGanhe,
        fetchConfigIndiqueGanhe,
        fetchRemoteConfigCupom,
        fetchRemoteConfigTaxaConv,
        loadDisabledHighlights,
        setConfigIndiqueGanhe,
        setProdStripe,
        prodStripe,
        disabledFeatures,
        telaNovaCupom,
        ativaTaxaConv,
        fetchTextoIndParcDebCombustivel,
        textoIndParcDebCombustivel,
        fetchPixDebitoVeicular,
        pixDebitoVeicular,
        exibeBannerPosteEGanhe,
        fetchBannerFlags,
        fetchPrazoDebitosPayment,
        prazoDebitosPayment,
        fetchValorMaximoEmprestimo,
        valorEmprestimo,
      }}>
      {children}
    </GlobalContext.Provider>
  );
}

function useGlobal() {
  const context = useContext(GlobalContext);
  return context;
}

export {useGlobal, GlobalProvider};
